import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { getSessionStorage } from "../../Helper";
import { API_BASE_URL } from "../../Config";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material UI components
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTheme } from "@mui/material";

function ServiceablePincodes() {
  const showSidebar = useSelector((state) => state.sidebar.showSidebar);
  const [pincode, setPincode] = useState("");
  const [pincodeData, setPincodeData] = useState(null);
  const [notServiceable, setNotServiceable] = useState(false);
  const [pincodeError, setPincodeError] = useState("");
  const [lastPincode, setLastPincode] = useState("");
  const [loading, setLoading] = useState(false);
  const [city, setCity] = useState("");

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const handlePincodeChange = (event) => {
    const newPincode = event.target.value;
    setPincode(newPincode);
    const pincodeRegex = /^[1-9]{1}[0-9]{2}\s{0,1}[0-9]{3}$/;
    if (!pincodeRegex.test(newPincode)) {
      setPincodeError("Please enter a valid Indian Pincode.");
    } else {
      setPincodeError("");
    }
  };

  const handleCheckPincode = async () => {
    if (pincodeError || !pincode) {
      return;
    }
    setLoading(true);

    try {
      const obj1 = {
        cid: getSessionStorage("cid"),
        pincode: pincode,
      };

      const cityResponse = await axios.post(`${API_BASE_URL}/pincode/getcity`, obj1);

      if (!cityResponse.data || cityResponse.data[0].Response === "Failed") {
        setNotServiceable(true);
        setPincodeData(null);
        setLastPincode(pincode);
      } else {
        const city = cityResponse.data[0].AREANAME;
        setCity(city);

        const obj2 = {
          cid: getSessionStorage("cid"),
          pincode: pincode,
          cityname: city,
        };

        const districtResponse = await axios.post(`${API_BASE_URL}/pincode/getdistrict`, obj2);

        if (
          !districtResponse.data ||
          districtResponse.data[0].Response === "Failed" ||
          districtResponse.data[0].DISABLED === "Yes"
        ) {
          setNotServiceable(true);
          setPincodeData(null);
          setLastPincode(pincode);
        } else {
          setPincodeData(districtResponse);
          setNotServiceable(false);
        }
      }
    } catch (error) {
      console.error(error);
      setNotServiceable(true);
      setPincodeData(null);
      setLastPincode(pincode);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleCheckPincode();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        className={`mt-[24%] sm:mt-10 md:mt-0 lg:mt-0 2xl:mt-0 h-[80%] ${
          showSidebar
            ? "transition-all transition-slowest ease ml-20 lg:ml-[18%]"
            : "transition-all transition-slowest ease"
        }`}
      >
        <MDBox display="flex" justifyContent="center" mt={4} mb={2} px={2}>
          <Card sx={{ boxShadow: 3, mt: 3, width: 467, borderRadius: "lg" }}>
            <MDBox
              component="form"
              width={{ xs: "100%", sm: "80%", md: "50%", lg: "100%" }}
              p={3}
              // shadow="lg"
              borderRadius="lg"
              // bgColor="white"
            >
              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text">
                  Enter Pincode <span style={{ color: "red" }}>*</span>
                </MDTypography>
                <MDInput
                  type="text"
                  placeholder="Enter Pincode"
                  value={pincode}
                  onChange={handlePincodeChange}
                  onKeyDown={handleEnterKeyPress}
                  fullWidth
                  error={!!pincodeError}
                  helperText={
                    pincodeError ? (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          color: isDark ? "#ffffffcc" : "#d32f2f",
                          fontSize: "0.75rem",
                        }}
                      >
                        <FontAwesomeIcon icon={faExclamationCircle} />
                        {pincodeError}
                      </span>
                    ) : (
                      ""
                    )
                  }
                  sx={{ "& .MuiInputBase-root": { height: "40px" } }}
                />
              </MDBox>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleCheckPincode}
                disabled={!pincode || !!pincodeError}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {loading ? "Loading..." : "Search"}
              </MDButton>
            </MDBox>
          </Card>
        </MDBox>
        <MDBox display="flex" justifyContent="center" mt={4} px={2}>
          <MDBox width={{ xs: "100%", sm: "80%", md: "50%", lg: "40%" }}>
            {notServiceable && (
              <Alert severity="error">
                <strong>Pincode:</strong> {lastPincode} is not serviceable!
              </Alert>
            )}
            {pincodeData && !notServiceable && (
              <Card sx={{ mb: 4, boxShadow: "lg", borderRadius: "lg" }}>
                <CardHeader title="Pincode Information" titleTypographyProps={{ variant: "h5" }} />
                <CardContent>
                  <MDBox display="flex" flexWrap="wrap">
                    <MDBox width={{ xs: "100%", md: "50%" }} mb={2}>
                      <MDTypography variant="body2" fontWeight="medium">
                        <strong>Pincode:</strong> {pincodeData.data[0].PINCODE}
                      </MDTypography>
                    </MDBox>
                    <MDBox width={{ xs: "100%", md: "50%" }} mb={2}>
                      <MDTypography variant="body2" fontWeight="medium">
                        <strong>Area Name:</strong> {pincodeData.data[0].AREANAME}
                      </MDTypography>
                    </MDBox>
                    <MDBox width={{ xs: "100%", md: "50%" }} mb={2}>
                      <MDTypography variant="body2" fontWeight="medium">
                        <strong>City Name:</strong> {pincodeData.data[0].CITYNAME}
                      </MDTypography>
                    </MDBox>
                    <MDBox width={{ xs: "100%", md: "50%" }} mb={2}>
                      <MDTypography variant="body2" fontWeight="medium">
                        <strong>District Name:</strong> {pincodeData.data[0].DISTRICTNAME}
                      </MDTypography>
                    </MDBox>
                    <MDBox width={{ xs: "100%", md: "50%" }} mb={2}>
                      <MDTypography variant="body2" fontWeight="medium">
                        <strong>State Name:</strong> {pincodeData.data[0].STATENAME}
                      </MDTypography>
                    </MDBox>
                    <MDBox width={{ xs: "100%", md: "50%" }} mb={2}>
                      <MDTypography variant="body2" fontWeight="medium">
                        <strong>Delivery Branch:</strong> {pincodeData.data[0].DELIVERYBRANCH}
                      </MDTypography>
                    </MDBox>
                    <MDBox width={{ xs: "100%", md: "50%" }} mb={2}>
                      <MDTypography variant="body2" fontWeight="medium">
                        <strong>ODA:</strong> {pincodeData.data[0].ODA}
                      </MDTypography>
                    </MDBox>
                    <MDBox width={{ xs: "100%", md: "50%" }} mb={2}>
                      <MDTypography variant="body2" fontWeight="medium">
                        <strong>Delivery TAT:</strong> {pincodeData.data[0].DELTAT} days
                      </MDTypography>
                    </MDBox>
                    <MDBox width={{ xs: "100%", md: "50%" }} mb={2}>
                      <MDTypography variant="body2" fontWeight="medium">
                        <strong>Distance:</strong> {pincodeData.data[0].DISTANCE} kms
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </CardContent>
              </Card>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default ServiceablePincodes;
